import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from '@shared/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListBundlesQuery, useListProfilesQuery } from '../../store/api/rpcSlice';

// Helper to get profile-specific bundle ID from localStorage
const getProfileBundleId = (profileSlug: string): string | null => {
  return localStorage.getItem(`profileEditor.selectedBundleId.${profileSlug}`);
};

export default function ProfileListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch profiles and bundles
  const { data: profilesData, isLoading: profilesLoading } = useListProfilesQuery(undefined);
  const profiles = profilesData?.profiles || [];
  const { data: bundlesData } = useListBundlesQuery({});
  const bundles = bundlesData?.bundles || [];

  // Filter profiles by search query
  const filteredProfiles = useMemo(() => {
    if (!searchQuery.trim()) return profiles;
    const query = searchQuery.toLowerCase();
    return profiles.filter(
      (p) =>
        p.slug.toLowerCase().includes(query) ||
        p.base_prompt_slug.toLowerCase().includes(query) ||
        p.middleware_names.some((name) => name.toLowerCase().includes(query))
    );
  }, [profiles, searchQuery]);

  if (profilesLoading) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Profile Editor
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage profiles and their middleware stacks. Select a profile to edit prompts. Bundle
          selection is per-profile.
        </Typography>
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search profiles by slug or middleware name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: <Box sx={{ mr: 1 }}>🔍</Box>,
        }}
      />

      {/* Profile Cards Grid */}
      <Grid container spacing={3}>
        {filteredProfiles.map((profile) => (
          <Grid item xs={12} sm={6} md={4} key={profile.slug}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
              onClick={() => navigate(`/admin/profiles/${profile.slug}`)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Typography variant="h6">{profile.slug}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {(() => {
                      const bundleId = getProfileBundleId(profile.slug);
                      const activeBundle = bundleId
                        ? bundles.find((b) => b.id === bundleId && !b.archived_at)
                        : null;
                      if (activeBundle) {
                        return (
                          <Chip label={`Bundle: ${activeBundle.name}`} color="info" size="small" />
                        );
                      }
                      if (profile.has_draft_edits) {
                        return <Chip label="Draft" color="warning" size="small" />;
                      }
                      return null;
                    })()}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Base: {profile.base_prompt_slug}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {profile.middleware_count} middleware(s)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {profile.middleware_names.slice(0, 3).map((name) => (
                    <Chip key={name} label={name} variant="outlined" size="small" />
                  ))}
                  {profile.middleware_names.length > 3 && (
                    <Chip
                      label={`+${profile.middleware_names.length - 3}`}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
